import capitalize from 'lodash/capitalize'
import throttle from 'lodash/throttle'
import moment from 'moment'
import api from '@/api'
import d2c from '@/d2c'
import logger from '@/logger'
import router from '@/router'
import store from '@/store'

// throttle updating store on stats events
const statsCache = {hostStats: {}, containerStats: {}}
const throttledCommitStats = throttle(commitStats, 1000, {leading: false})
function commitStats() {
  const newStats = {}
  for (const collection in statsCache) {
    newStats[collection] = {...store.state[collection], ...statsCache[collection]}
  }
  store.commit('update', newStats)
  statsCache.hostStats = {}
  statsCache.containerStats = {}
}

export default {
  update({commit}, changes) {
    commit('update', changes)
  },
  login({commit, getters}, token) {
    logger.success('✋ Login')
    commit('update', {token})
    if (router.currentRoute.query.redirect) {
      router.push(router.currentRoute.query.redirect).catch(() => {})
    } else {
      router.push(getters.config.homePath).catch(() => {})
    }
  },
  use({commit, getters}, accountId) {
    const account = getters.accounts.find(account => account.id === accountId)
    logger.success(`✋ Use ${account.member.name} in ${account.name}`)
    commit('update', {token: account.member.token})
    api.reconnect()
  },
  logout({commit}) {
    logger.success('✋ Logout')
    logger.closeAllNotifications()
    commit('update', {
      token: '',
      instances: [], regions: [], tariffs: [],
      account: {}, member: {}, user: {},
      members: [], roles: [],
      containers: [], hosts: [], keys: [], projects: [], providers: [], services: [],
      sessionConfig: {},
    })
    api.disconnect()
  },
  updateConfig({state, commit}, config) {
    const newConfig = {...state.userConfig}
    for (const prop in config) {
      if (typeof config[prop] === 'undefined') {
        delete newConfig[prop]
      } else {
        newConfig[prop] = config[prop]
      }
    }
    commit('update', {userConfig: newConfig})
  },
  updateSession({state, commit}, config) {
    const newConfig = {...state.sessionConfig}
    for (const prop in config) {
      if (typeof config[prop] === 'undefined') {
        delete newConfig[prop]
      } else {
        newConfig[prop] = config[prop]
      }
    }
    commit('update', {sessionConfig: newConfig})
  },
  goal(context, {goal, params}) {
    if (!d2c.goals.includes(goal)) {
      logger.warning(`Reached unknown goal: ${goal}`, params || {})
    } else {
      logger.verbose(`Reached goal: ${goal}`, params || {})
    }
  },
  socket_connect({commit}) {
    logger.event('⚡ Socket connected')
    commit('update', {connected: true})
  },
  socket_reconnect() {
    logger.event('⚡ Socket reconnected')
    api.subscriptions.updateHostStats()
    api.subscriptions.updateContainerStats()
  },
  socket_disconnect({commit}) {
    logger.event('⚡ Socket disconnected')
    commit('update', {connected: false})
    api.subscriptions.subscribedHostIds = []
    api.subscriptions.subscribedContainerIds = []
  },
  socket_logs(context, data) {
    logger.event('⚡ logs', data)
  },
  socket_stats(context, data) {
    logger.event('⚡ stats', data)
    statsCache[`${data.target}Stats`][data.id] = {...data.data, disk: data.data.ebs_disk || data.data.main_disk}
    throttledCommitStats()
  },
  socket_create({getters, commit}, data) {
    logger.event('⚡ create', {data})
    commit('addEntity', data)
    const entity = getters[`${data.target}sById`][data.id]
    const isNewServiceContainer = data.target === 'container' && entity._service.nextProcess === 'DEPLOYING'
    if (entity && !isNewServiceContainer) {
      logger.notify({entity, type: 'success', text: `New ${data.target} has been created`})
    }
  },
  socket_delete({state, getters, commit}, data) {
    logger.event('⚡ delete', {data})
    const entity = getters[`${data.target}sById`] && getters[`${data.target}sById`][data.id]
    if (entity) {
      const target = capitalize(entity.target)
      const entityService = entity._service
      const isOldServiceContainer = data.target === 'container' && entityService && entityService.process === 'DESTROYING'
      if (!isOldServiceContainer) {
        const type = 'success'
        const text = `${target} has been deleted`
        logger.notify({entity, type, text})
      }
      if (state.route.path === `/${data.target}s/${data.id}` || state.route.path === `/${data.target}s/${data.name}`) {
        logger.verbose(`The ${data.target} "${data.name}" is deleted, so you will be redirected to Dashboard`)
        router.push(getters.config.homePath)
      }
      commit('removeEntity', entity)
    }
  },
  socket_update({getters, commit}, data) {
    logger.event('⚡ update', {data})
    const targetsWithProcess = ['service', 'host', 'container']
    const entitiesById = getters[`${data.target}sById`]
    let entity = null
    if (entitiesById) {
      entity = entitiesById[data.id]
      if (entity) {
        // reset the timer if a new process starts
        if (entity.process && entity.process !== data.process) {
          data.processStarted = data.processStarted || moment().format()
        }
        commit('updateEntity', data)
      }
    } else {
      entity = getters[data.target]
      if (entity) {
        const newEntity = {}
        newEntity[data.target] = Object.assign({}, entity, data)
        commit('update', newEntity)
      }
    }
    // show notification
    if (targetsWithProcess.includes(data.target)) {
      if (data.lastResult && entity && entity.process && entity.process !== 'AWAITING_HOSTS') {
        const process = capitalize(entity.process).replace(/_/g, ' ')
        const type = data.lastResult === 'SUCCESS' ? 'success' : (data.lastResult === 'FAIL' ? 'error' : 'info')
        const result = data.lastResult === 'SUCCESS' ? 'successfully completed' : 'completed with errors'
        const text = `${process} ${result}`
        logger.notify({entity, type, text})
      }
    } else {
      // TODO: refactor this exceptions
      if (data.target === 'project' && !data.name) {
        return false
      }
      if (data.target === 'user' && data.data) {
        return false
      }
      logger.notify({entity, type: 'success', text: `Updated`})
    }
    // update data if role's or member's actions are changed
    if ((data.target === 'member' && data.id === store.getters.member.id) || (data.target === 'role' && data.id === store.getters.role.id)) {
      api.fetchData()
    }
  },
  socket_rulesUpdate(context, data) {
    logger.event('⚡ rulesUpdate', {data})
    api.fetchData()
  },
  socket_refreshPage(context, data) {
    logger.event('⚡ refreshPage', {data})
  },
  socket_termData(context, data) {
    logger.event('⚡ termData', {data})
  },
  socket_termEnd(context, data) {
    logger.event('⚡ termEnd', {data})
  },
}
